import {
  LayoutDashboard,
  ClipboardList,
  CalendarCheck,
  Box,
  Package,
  BarChart3,
  Syringe,
  Wrench,
  Play,
  Cpu,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import type { Permission } from '@/lib/permissions';

export interface NavItem {
  labelKey: string;    // i18n key (e.g. 'common:nav.work_orders')
  href: string;        // absolute path e.g. '/maintenance/work-orders'
  icon: React.ElementType;
  badge?: boolean;     // drives the open-WO count bubble
  permission?: Permission;
}

export interface ModuleConfig {
  id: 'maintenance' | 'inject' | 'quality';
  labelKey: string;    // i18n key (e.g. 'common:nav.maintenance')
  icon: React.ElementType;
  baseRoute: string;
  accentColor: 'blue' | 'emerald' | 'violet';  // semantic token — never used as Tailwind fragment
  navItems: NavItem[];
  permission?: Permission;          // gates the entire module by role
}

export const MODULES: ModuleConfig[] = [
  {
    id: 'maintenance',
    labelKey: 'common:nav.maintenance',
    icon: Wrench,
    baseRoute: '/maintenance',
    accentColor: 'blue',
    navItems: [
      { labelKey: 'common:nav.dashboard',     href: '/maintenance',              icon: LayoutDashboard },
      { labelKey: 'common:nav.work_orders',   href: '/maintenance/work-orders',  icon: ClipboardList, badge: true },
      { labelKey: 'maintenance:nav.planning', href: '/maintenance/readiness',    icon: CalendarCheck },
      { labelKey: 'common:nav.molds',         href: '/maintenance/molds',        icon: Box },
      { labelKey: 'common:nav.spare_parts',   href: '/maintenance/spare-parts',  icon: Package },
      { labelKey: 'common:nav.reports',       href: '/maintenance/reports',      icon: BarChart3, permission: 'reports:view' },
    ],
  },
  {
    id: 'inject',
    labelKey: 'common:nav.injection',
    icon: Syringe,
    baseRoute: '/inject',
    accentColor: 'emerald',
    permission: 'inject:view',
    navItems: [
      { labelKey: 'common:nav.dashboard',      href: '/inject',            icon: LayoutDashboard },
      { labelKey: 'injection:nav.production',  href: '/inject/production', icon: Play, badge: true },
      { labelKey: 'injection:nav.parts',       href: '/inject/parts',      icon: Package },
      { labelKey: 'injection:nav.machines',    href: '/inject/machines',   icon: Cpu },
      { labelKey: 'common:nav.reports',        href: '/inject/reports',    icon: BarChart3, permission: 'inject:manage' as Permission },
    ],
  },
  {
    id: 'quality',
    labelKey: 'common:nav.quality',
    icon: ShieldCheck,
    baseRoute: '/quality',
    accentColor: 'violet',
    permission: 'quality:view' as Permission,
    navItems: [
      { labelKey: 'common:nav.dashboard',    href: '/quality',          icon: LayoutDashboard },
      { labelKey: 'quality:nav.ncs',         href: '/quality/nc',       icon: ClipboardList },
      { labelKey: 'quality:nav.scrap',       href: '/quality/scrap',    icon: Trash2 },
    ],
  },
];

/**
 * Derive the active module from the current URL pathname.
 * Falls back to maintenance if no module matches.
 */
export function getActiveModule(pathname: string): ModuleConfig {
  return (
    MODULES.find(
      m => pathname === m.baseRoute || pathname.startsWith(m.baseRoute + '/'),
    ) ?? MODULES[0]
  );
}

/**
 * Explicit accent class lookup — never build Tailwind classes from dynamic strings
 * (they won't survive the content scanner / purge step).
 */
export const MODULE_ACCENT: Record<
  ModuleConfig['id'],
  { pill: string; dot: string }
> = {
  maintenance: {
    pill: 'bg-blue-500/10 text-blue-800 border-blue-500/20 dark:text-blue-300 dark:border-blue-400/20',
    dot:  'bg-blue-600',
  },
  inject: {
    pill: 'bg-emerald-500/10 text-emerald-800 border-emerald-500/20 dark:text-emerald-300 dark:border-emerald-400/20',
    dot:  'bg-emerald-600',
  },
  quality: {
    pill: 'bg-violet-500/10 text-violet-800 border-violet-500/20 dark:text-violet-300 dark:border-violet-400/20',
    dot:  'bg-violet-600',
  },
};
